/**
 * Which surface is rendering the public checkout flow.
 *
 * The three differ along two independent axes — how a waiver gets signed, and
 * whether a host page is listening — so they are NOT a spectrum from the plain
 * page to the embed:
 *
 * - `page`   — the full public event page (`/events/...`). Popup waiver
 *              signing, `router.push` to `/confirmation`. The default.
 * - `embed`  — the school's own site via the widget iframe (U3). Inline
 *              signing, postMessage lifecycle, in-modal confirmation, no
 *              navigation (the modal would blank).
 * - `mobile` — the mobile app's in-app browser, opened from the native
 *              ticketed-event screen. Inline signing like the embed (an in-app
 *              browser cannot reliably open a popup), but ordinary navigation
 *              like the page: nothing is listening for postMessage, and
 *              `/confirmation` is the only surface that hosts
 *              `before_attendance` forms — a gap the embed accepts and mobile
 *              has no reason to inherit.
 */
export type CheckoutSurface = 'page' | 'embed' | 'mobile'

/** Query param on the `/checkout` route that selects a non-embed surface. */
export const CHECKOUT_SURFACE_PARAM = 'src'

/**
 * Resolve the surface for the `/checkout` route. That route exists for the
 * embed widget, so `embed` is the default and an unknown/absent `src` falls
 * back to it — an attacker-supplied value can only pick from the allowlist.
 *
 * A `switch` rather than an object lookup on purpose: `param` comes straight
 * off the query string, and `SURFACE_BY_PARAM['__proto__']` would return
 * `Object.prototype` — truthy, so `??` never fires and a junk object flows on
 * as the surface. `page` is intentionally NOT selectable: it is the full event
 * page's surface, and honoring it inside a frame would mean popup waiver
 * signing in a context that cannot open one.
 */
export function resolveCheckoutSurface(param: string | undefined): CheckoutSurface {
  switch (param) {
    case 'mobile':
      return 'mobile'
    default:
      return 'embed'
  }
}

/**
 * The order-create `source` tag for a surface, or `null` when the surface
 * sends none (the plain page path is unchanged and stays absent from the logs).
 *
 * Kept here rather than inline at the fetch so the values the backend logs live
 * in one greppable place. The backend accepts any string ≤32 chars
 * (`source: z.string().max(32).optional()`) and never persists it, so adding a
 * surface needs no backend change — but `mobile_app` MUST stay distinct from
 * `embed` or mobile orders silently inflate the embed widget's open→complete
 * adoption metric (R11), which is that probe's whole go/no-go signal.
 */
export function attributionSource(surface: CheckoutSurface): string | null {
  switch (surface) {
    case 'embed':
      return 'embed'
    case 'mobile':
      return 'mobile_app'
    case 'page':
      return null
  }
}
